
// src/pages/VisitReport/VisitReportListPage.js
// All visit reports (mine / team / one executive) with revisit badges + detail.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Plus, X, MapPin, Pencil, Trash2 } from 'lucide-react';
import { selectAuthState } from '../../store/selectors/authSelector';
import {
  fetchMyReports,
  fetchAllReports,
  updateVisitReport,
  deleteVisitReport,
} from '../../services/features/visitReport/visitReportSlice';
import { Modal, Button, ConfirmDialog, toast } from '../../components/ui/UI';
import RangeBar from '../../components/Report/RangeBar';
import {
  computeRange,
  formatDateTime,
  ordinal,
  FLOW_META,
} from '../../utils/attendanceFormat';
import '../Shared/ReportPages.css';

const PRESETS = [
  { key: 'all', label: 'All Time' },
  { key: 'today', label: 'Today' },
  { key: 'week', label: '7 Days' },
  { key: 'month', label: 'This Month' },
  { key: 'custom', label: 'Custom' },
];

const FLOW_FILTERS = [
  { key: '', label: 'All' },
  { key: 'COLD', label: 'Cold' },
  { key: 'WARM', label: 'Warm' },
  { key: 'HOT', label: 'Hot' },
];

const PAGE_SIZE = 15;
const matchLabel = (m) => (m === 'shop' ? 'SHOP' : 'MOBILE');

const FlowPill = ({ flow }) => {
  const m = FLOW_META[flow] || FLOW_META.WARM;
  return (
    <span className="rp-pill" style={{ background: m.bg, color: m.color }}>
      {m.label.toUpperCase()}
    </span>
  );
};

const RevisitPill = ({ item }) =>
  item.isRevisit ? (
    <span className="rp-pill rp-revisit">
      REVISIT · {ordinal(item.visitNumber)}
      {item.matchedOn?.length ? ` · ${item.matchedOn.map(matchLabel).join(' + ')}` : ''}
    </span>
  ) : null;

const Field = ({ label, children }) => (
  <div>
    <div className="rp-field-label">{label}</div>
    <div className="rp-field-value">{children || '—'}</div>
  </div>
);

/* ═════════════════════════ Detail / edit modal ═════════════════════════ */
const DetailModal = ({ item, onClose, role, user }) => {
  const dispatch = useDispatch();
  const isAdmin = role === 'Admin';
  const authorId = item.executiveId?._id || item.executiveId;
  const isAuthor = !!authorId && !!user?._id && String(authorId) === String(user._id);
  const canEdit = isAdmin || isAuthor;
  const canNote = isAdmin || (!isAuthor && role === 'MarketingManager');

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [form, setForm] = useState(null);
  const [note, setNote] = useState(item.managerNote || '');

  useEffect(() => {
    setNote(item.managerNote || '');
  }, [item.managerNote, item._id]);

  const startEdit = () => {
    setForm({
      name: item.name || '',
      businessName: item.businessName || '',
      mobile: item.mobile || '',
      taluk: item.taluk || '',
      district: item.district || '',
      business: item.business || '',
      statusFlow: item.statusFlow || 'WARM',
      report: item.report || '',
    });
    setEditing(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!/^[6-9]\d{9}$/.test(String(form.mobile).trim())) {
      toast.error('Enter a valid 10-digit mobile');
      return;
    }
    if (!form.name.trim() || !form.businessName.trim() || !form.report.trim()) {
      toast.error('Name, business name and report are required');
      return;
    }
    setSaving(true);
    try {
      const data = { ...form };
      if (isAdmin) data.managerNote = note;
      await dispatch(updateVisitReport({ id: item._id, data })).unwrap();
      toast.success('Report updated');
      setEditing(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const saveNote = async () => {
    setSaving(true);
    try {
      await dispatch(updateVisitReport({ id: item._id, data: { managerNote: note } })).unwrap();
      toast.success('Manager note saved');
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await dispatch(deleteVisitReport(item._id)).unwrap();
      toast.success('Report deleted');
      onClose();
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to delete');
    }
  };

  const loc = item.location;
  const hasLoc = loc && typeof loc.latitude === 'number';
  const by = item.executiveName || item.executiveId?.name;
  const byRole = item.executiveRole || item.executiveId?.role;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="lg"
        title={`${item.name} · ${item.businessName}`}
        footer={
          <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', width: '100%' }}>
            <div>
              {canEdit && (
                <Button variant="ghost" icon={<Trash2 size={14} />} onClick={() => setConfirmDel(true)}>
                  Delete
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              {editing ? (
                <>
                  <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                  <Button loading={saving} onClick={save}>Save Changes</Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={onClose}>Close</Button>
                  {canEdit && (
                    <Button icon={<Pencil size={14} />} onClick={startEdit}>Edit Report</Button>
                  )}
                </>
              )}
            </div>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <FlowPill flow={item.statusFlow} />
            <RevisitPill item={item} />
          </div>

          {item.isRevisit && (
            <div className="rp-revisitbox">
              REVISIT · {ordinal(item.visitNumber)} visit · {item.totalVisits} total for this customer
              {item.matchedOn?.length
                ? ` (matched by ${item.matchedOn.map((m) => (m === 'shop' ? 'shop name' : 'mobile')).join(' + ')})`
                : ''}
            </div>
          )}

          {editing ? (
            <div className="rp-form-grid">
              {[
                ['name', 'Customer name'],
                ['businessName', 'Shop / business name'],
                ['mobile', 'Mobile'],
                ['business', 'Business type'],
                ['taluk', 'Taluk'],
                ['district', 'District'],
              ].map(([k, label]) => (
                <div key={k}>
                  <div className="rp-field-label">{label}</div>
                  <input
                    className="rp-input"
                    style={{ width: '100%', marginTop: 4 }}
                    value={form[k]}
                    maxLength={k === 'mobile' ? 10 : undefined}
                    onChange={(e) => set(k, k === 'mobile' ? e.target.value.replace(/\D/g, '') : e.target.value)}
                  />
                </div>
              ))}
              <div className="full">
                <div className="rp-field-label">Lead status</div>
                <div className="rp-flow" style={{ marginTop: 6 }}>
                  {Object.entries(FLOW_META).map(([k, m]) => (
                    <button
                      key={k}
                      type="button"
                      className={`rp-flowbtn ${form.statusFlow === k ? 'active' : ''}`}
                      style={{ '--c': m.color, '--cbg': m.bg }}
                      onClick={() => set('statusFlow', k)}
                    >
                      <b>{m.label.toUpperCase()}</b>
                      <small>{m.hint}</small>
                    </button>
                  ))}
                </div>
              </div>
              <div className="full">
                <div className="rp-field-label">Report</div>
                <textarea
                  className="rp-input"
                  style={{ width: '100%', height: 130, padding: 10, marginTop: 4, resize: 'vertical' }}
                  value={form.report}
                  onChange={(e) => set('report', e.target.value)}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="rp-detail-grid">
                <Field label="Mobile">{item.mobile}</Field>
                <Field label="Business type">{item.business}</Field>
                <Field label="Location">{`${item.taluk}, ${item.district}`}</Field>
                <Field label="Submitted">{formatDateTime(item.submittedAt)}</Field>
                <Field label="Submitted by">
                  {by ? `${by}${byRole ? ` (${byRole})` : ''}` : '—'}
                </Field>
                <Field label="GPS">
                  {hasLoc ? (
                    <a
                      className="rp-link"
                      href={`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MapPin size={12} style={{ verticalAlign: -1 }} /> {loc.latitude.toFixed(4)},{' '}
                      {loc.longitude.toFixed(4)} (±{Math.round(loc.accuracy || 0)}m)
                    </a>
                  ) : (
                    'Not captured'
                  )}
                </Field>
              </div>

              <div>
                <div className="rp-field-label" style={{ marginBottom: 6 }}>Report</div>
                <div className="rp-box rp-reportbox">{item.report}</div>
              </div>
            </>
          )}

          {/* Manager note */}
          {(canNote || item.managerNote) && !editing && (
            <div>
              <div className="rp-field-label" style={{ marginBottom: 6 }}>Manager note</div>
              {canNote ? (
                <>
                  <textarea
                    className="rp-input"
                    style={{ width: '100%', height: 80, padding: 10, resize: 'vertical' }}
                    placeholder="Add a note for this visit…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <div style={{ marginTop: 8 }}>
                    <Button size="sm" variant="ghost" loading={saving} onClick={saveNote}>
                      Save note
                    </Button>
                  </div>
                </>
              ) : (
                <div className="rp-box rp-reportbox">{item.managerNote}</div>
              )}
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDel}
        onClose={() => setConfirmDel(false)}
        onConfirm={remove}
        title="Delete report?"
        message="This cannot be undone."
        confirmLabel="Delete"
      />
    </>
  );
};

/* ═════════════════════════════ List page ═════════════════════════════ */
const VisitReportListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { role, user } = useSelector(selectAuthState);
  const { myList, allList, loading } = useSelector((s) => s.visitReport);

  const isAdmin = role === 'Admin';
  const canSeeTeam = isAdmin || role === 'MarketingManager';
  const canCreate = role === 'MarketingManager' || role === 'MarketingExecutive';

  const fExecId = sp.get('executiveId') || '';
  const fExecName = sp.get('name') || '';
  const pFrom = sp.get('from') || '';
  const pTo = sp.get('to') || '';
  const pLabel = sp.get('label') || '';

  const [range, setRange] = useState(() => {
    const byLabel = PRESETS.find((p) => p.label === pLabel)?.key;
    return byLabel || (pFrom || pTo ? 'custom' : 'all');
  });
  const [customFrom, setCustomFrom] = useState(pFrom);
  const [customTo, setCustomTo] = useState(pTo);
  const [scope, setScope] = useState(isAdmin ? 'team' : 'mine');
  const [flow, setFlow] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { from, to } = useMemo(
    () => computeRange(range, customFrom, customTo),
    [range, customFrom, customTo]
  );

  const teamMode = !!fExecId || (canSeeTeam && scope === 'team');

  const load = useCallback(() => {
    const params = {
      statusFlow: flow || undefined,
      search: debounced || undefined,
      executiveId: fExecId || undefined,
      from: from || undefined,
      to: to || undefined,
      limit: 200,
    };
    dispatch(teamMode ? fetchAllReports(params) : fetchMyReports(params));
  }, [dispatch, teamMode, flow, debounced, fExecId, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => setPage(1), [flow, debounced, range, scope, fExecId]);

  const data = teamMode ? allList : myList;
  const totalPages = Math.max(1, Math.ceil(data.length / PAGE_SIZE));
  const rows = data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const opened = data.find((x) => x._id === openId) || null;

  const clearExecutive = () => {
    const next = new URLSearchParams(sp);
    next.delete('executiveId');
    next.delete('name');
    setSp(next, { replace: true });
  };

  return (
    <div className="rp-page">
      <div className="rp-toolbar">
        {canSeeTeam && !isAdmin && !fExecId && (
          <div className="rp-chips">
            <button className={`rp-chip ${scope === 'mine' ? 'active' : ''}`} onClick={() => setScope('mine')}>
              My Reports
            </button>
            <button className={`rp-chip ${scope === 'team' ? 'active' : ''}`} onClick={() => setScope('team')}>
              Team Reports
            </button>
          </div>
        )}
        {fExecId && (
          <button className="rp-filterpill" onClick={clearExecutive} title="Clear filter">
            {fExecName || 'Executive'} <X size={13} />
          </button>
        )}
        <div className="rp-spacer" />
        {canSeeTeam && (
          <button className="rp-chip" onClick={() => navigate('/visit-reports')}>← Dashboard</button>
        )}
        {canCreate && (
          <button className="rp-chip active" onClick={() => navigate('/visit-reports/new')}>
            <Plus size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
            New Report
          </button>
        )}
      </div>

      <RangeBar
        presets={PRESETS}
        range={range}
        onRange={setRange}
        from={customFrom}
        to={customTo}
        onFrom={setCustomFrom}
        onTo={setCustomTo}
      />

      <div className="rp-toolbar">
        <div className="rp-chips">
          {FLOW_FILTERS.map((f) => (
            <button
              key={f.key || 'all'}
              className={`rp-chip ${flow === f.key ? 'active' : ''}`}
              onClick={() => setFlow(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="rp-spacer" />
        <div className="rp-search">
          <Search size={14} />
          <input
            className="rp-input"
            placeholder="Search name, mobile, taluk…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="rp-card">
        {loading && data.length === 0 ? (
          <div className="rp-loading">Loading…</div>
        ) : data.length === 0 ? (
          <div className="rp-empty">
            <b>No reports found</b>
            {teamMode
              ? 'Reports submitted by your team will appear here.'
              : 'Submit your first customer visit report to see it here.'}
          </div>
        ) : (
          <div className="rp-table-wrap">
            <table className="rp-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Mobile</th>
                  <th>Location</th>
                  <th>Lead</th>
                  <th>Visit</th>
                  {teamMode && <th>Submitted by</th>}
                  <th>Date</th>
                  <th>Report</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id} className="clickable" onClick={() => setOpenId(r._id)}>
                    <td>
                      <div className="rp-strong">{r.name}</div>
                      <div className="rp-sub">{r.businessName}</div>
                    </td>
                    <td>{r.mobile}</td>
                    <td>
                      {r.taluk}
                      <div className="rp-sub">{r.district}</div>
                    </td>
                    <td><FlowPill flow={r.statusFlow} /></td>
                    <td>
                      {r.isRevisit ? <RevisitPill item={r} /> : <span className="rp-muted">1st visit</span>}
                    </td>
                    {teamMode && (
                      <td>
                        {r.executiveName || r.executiveId?.name || '—'}
                        <div className="rp-sub">{r.executiveRole || r.executiveId?.role}</div>
                      </td>
                    )}
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.submittedAt)}</td>
                    <td><div className="rp-snippet">{r.report}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data.length > PAGE_SIZE && (
          <div className="rp-pager">
            <span>
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, data.length)} of {data.length}
            </span>
            <div className="rp-pager-btns">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</button>
              <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {opened && (
        <DetailModal item={opened} role={role} user={user} onClose={() => setOpenId(null)} />
      )}
    </div>
  );
};

export default VisitReportListPage;

//------------ 13.10.26 Backup --------------
// // src/pages/VisitReport/VisitReportListPage.js
// // All visit reports (mine / team / one executive) with revisit badges + detail.
// import React, { useCallback, useEffect, useMemo, useState } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import { useNavigate, useSearchParams } from 'react-router-dom';
// import { Search, Plus, X, MapPin, Pencil, Trash2 } from 'lucide-react';
// import { selectAuthState } from '../../store/selectors/authSelector';
// import {
//   fetchMyReports,
//   fetchAllReports,
//   updateVisitReport,
//   deleteVisitReport,
// } from '../../services/features/visitReport/visitReportSlice';
// import { Modal, Button, ConfirmDialog, toast } from '../../components/ui/UI';
// import RangeBar from '../../components/Report/RangeBar';
// import {
//   computeRange,
//   formatDateTime,
//   ordinal,
//   FLOW_META,
// } from '../../utils/attendanceFormat';
// import '../Shared/ReportPages.css';

// const PRESETS = [
//   { key: 'all', label: 'All Time' },
//   { key: 'today', label: 'Today' },
//   { key: 'week', label: '7 Days' },
//   { key: 'month', label: 'This Month' },
//   { key: 'custom', label: 'Custom' },
// ];

// const FLOW_FILTERS = [
//   { key: '', label: 'All' },
//   { key: 'COLD', label: 'Cold' },
//   { key: 'WARM', label: 'Warm' },
//   { key: 'HOT', label: 'Hot' },
// ];

// const PAGE_SIZE = 15;
// const matchLabel = (m) => (m === 'shop' ? 'SHOP' : 'MOBILE');

// const FlowPill = ({ flow }) => {
//   const m = FLOW_META[flow] || FLOW_META.WARM;
//   return (
//     <span className="rp-pill" style={{ background: m.bg, color: m.color }}>
//       {m.label.toUpperCase()}
//     </span>
//   );
// };

// const RevisitPill = ({ item }) =>
//   item.isRevisit ? (
//     <span className="rp-pill rp-revisit">
//       REVISIT · {ordinal(item.visitNumber)}
//       {item.matchedOn?.length ? ` · ${item.matchedOn.map(matchLabel).join(' + ')}` : ''}
//     </span>
//   ) : null;

// const Field = ({ label, children }) => (
//   <div>
//     <div className="rp-field-label">{label}</div>
//     <div className="rp-field-value">{children || '—'}</div>
//   </div>
// );

// /* ═════════════════════════ Detail / edit modal ═════════════════════════ */
// const DetailModal = ({ item, onClose, role, user }) => {
//   const dispatch = useDispatch();
//   const isAdmin = role === 'Admin';
//   const authorId = item.executiveId?._id || item.executiveId;
//   const isAuthor = !!authorId && !!user?._id && String(authorId) === String(user._id);
//   const canEdit = isAdmin || isAuthor;
//   const canNote = isAdmin || (!isAuthor && role === 'MarketingManager');

//   const [editing, setEditing] = useState(false);
//   const [saving, setSaving] = useState(false);
//   const [confirmDel, setConfirmDel] = useState(false);
//   const [form, setForm] = useState(null);
//   const [note, setNote] = useState(item.managerNote || '');

//   useEffect(() => {
//     setNote(item.managerNote || '');
//   }, [item.managerNote, item._id]);

//   const startEdit = () => {
//     setForm({
//       name: item.name || '',
//       businessName: item.businessName || '',
//       mobile: item.mobile || '',
//       taluk: item.taluk || '',
//       district: item.district || '',
//       business: item.business || '',
//       statusFlow: item.statusFlow || 'WARM',
//       report: item.report || '',
//     });
//     setEditing(true);
//   };

//   const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

//   const save = async () => {
//     if (!/^[6-9]\d{9}$/.test(String(form.mobile).trim())) {
//       toast.error('Enter a valid 10-digit mobile');
//       return;
//     }
//     if (!form.name.trim() || !form.businessName.trim() || !form.report.trim()) {
//       toast.error('Name, business name and report are required');
//       return;
//     }
//     setSaving(true);
//     try {
//       const data = { ...form };
//       if (isAdmin) data.managerNote = note;
//       await dispatch(updateVisitReport({ id: item._id, data })).unwrap();
//       toast.success('Report updated');
//       setEditing(false);
//     } catch (e) {
//       toast.error(typeof e === 'string' ? e : 'Failed to update');
//     } finally {
//       setSaving(false);
//     }
//   };

//   const saveNote = async () => {
//     setSaving(true);
//     try {
//       await dispatch(updateVisitReport({ id: item._id, data: { managerNote: note } })).unwrap();
//       toast.success('Manager note saved');
//     } catch (e) {
//       toast.error(typeof e === 'string' ? e : 'Failed to save note');
//     } finally {
//       setSaving(false);
//     }
//   };

//   const remove = async () => {
//     try {
//       await dispatch(deleteVisitReport(item._id)).unwrap();
//       toast.success('Report deleted');
//       onClose();
//     } catch (e) {
//       toast.error(typeof e === 'string' ? e : 'Failed to delete');
//     }
//   };

//   const loc = item.location;
//   const hasLoc = loc && typeof loc.latitude === 'number';
//   const by = item.executiveName || item.executiveId?.name;
//   const byRole = item.executiveRole || item.executiveId?.role;

//   return (
//     <>
//       <Modal
//         open
//         onClose={onClose}
//         size="lg"
//         title={`${item.name} · ${item.businessName}`}
//         footer={
//           <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', width: '100%' }}>
//             <div>
//               {canEdit && (
//                 <Button variant="ghost" icon={<Trash2 size={14} />} onClick={() => setConfirmDel(true)}>
//                   Delete
//                 </Button>
//               )}
//             </div>
//             <div style={{ display: 'flex', gap: 10 }}>
//               {editing ? (
//                 <>
//                   <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
//                   <Button loading={saving} onClick={save}>Save Changes</Button>
//                 </>
//               ) : (
//                 <>
//                   <Button variant="ghost" onClick={onClose}>Close</Button>
//                   {canEdit && (
//                     <Button icon={<Pencil size={14} />} onClick={startEdit}>Edit Report</Button>
//                   )}
//                 </>
//               )}
//             </div>
//           </div>
//         }
//       >
//         <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
//           <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
//             <FlowPill flow={item.statusFlow} />
//             <RevisitPill item={item} />
//           </div>

//           {item.isRevisit && (
//             <div className="rp-revisitbox">
//               REVISIT · {ordinal(item.visitNumber)} visit · {item.totalVisits} total for this customer
//               {item.matchedOn?.length
//                 ? ` (matched by ${item.matchedOn.map((m) => (m === 'shop' ? 'shop name' : 'mobile')).join(' + ')})`
//                 : ''}
//             </div>
//           )}

//           {editing ? (
//             <div className="rp-form-grid">
//               {[
//                 ['name', 'Customer name'],
//                 ['businessName', 'Shop / business name'],
//                 ['mobile', 'Mobile'],
//                 ['business', 'Business type'],
//                 ['taluk', 'Taluk'],
//                 ['district', 'District'],
//               ].map(([k, label]) => (
//                 <div key={k}>
//                   <div className="rp-field-label">{label}</div>
//                   <input
//                     className="rp-input"
//                     style={{ width: '100%', marginTop: 4 }}
//                     value={form[k]}
//                     maxLength={k === 'mobile' ? 10 : undefined}
//                     onChange={(e) => set(k, k === 'mobile' ? e.target.value.replace(/\D/g, '') : e.target.value)}
//                   />
//                 </div>
//               ))}
//               <div className="full">
//                 <div className="rp-field-label">Lead status</div>
//                 <div className="rp-flow" style={{ marginTop: 6 }}>
//                   {Object.entries(FLOW_META).map(([k, m]) => (
//                     <button
//                       key={k}
//                       type="button"
//                       className={`rp-flowbtn ${form.statusFlow === k ? 'active' : ''}`}
//                       style={{ '--c': m.color, '--cbg': m.bg }}
//                       onClick={() => set('statusFlow', k)}
//                     >
//                       <b>{m.label.toUpperCase()}</b>
//                       <small>{m.hint}</small>
//                     </button>
//                   ))}
//                 </div>
//               </div>
//               <div className="full">
//                 <div className="rp-field-label">Report</div>
//                 <textarea
//                   className="rp-input"
//                   style={{ width: '100%', height: 130, padding: 10, marginTop: 4, resize: 'vertical' }}
//                   value={form.report}
//                   onChange={(e) => set('report', e.target.value)}
//                 />
//               </div>
//             </div>
//           ) : (
//             <>
//               <div className="rp-detail-grid">
//                 <Field label="Mobile">{item.mobile}</Field>
//                 <Field label="Business type">{item.business}</Field>
//                 <Field label="Location">{`${item.taluk}, ${item.district}`}</Field>
//                 <Field label="Submitted">{formatDateTime(item.submittedAt)}</Field>
//                 <Field label="Submitted by">
//                   {by ? `${by}${byRole ? ` (${byRole})` : ''}` : '—'}
//                 </Field>
//                 <Field label="GPS">
//                   {hasLoc ? (
//                     <a
//                       className="rp-link"
//                       href={`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`}
//                       target="_blank"
//                       rel="noopener noreferrer"
//                     >
//                       <MapPin size={12} style={{ verticalAlign: -1 }} /> {loc.latitude.toFixed(4)},{' '}
//                       {loc.longitude.toFixed(4)} (±{Math.round(loc.accuracy || 0)}m)
//                     </a>
//                   ) : (
//                     'Not captured'
//                   )}
//                 </Field>
//               </div>

//               <div>
//                 <div className="rp-field-label" style={{ marginBottom: 6 }}>Report</div>
//                 <div className="rp-box rp-reportbox">{item.report}</div>
//               </div>
//             </>
//           )}

//           {/* Manager note */}
//           {(canNote || item.managerNote) && !editing && (
//             <div>
//               <div className="rp-field-label" style={{ marginBottom: 6 }}>Manager note</div>
//               {canNote ? (
//                 <>
//                   <textarea
//                     className="rp-input"
//                     style={{ width: '100%', height: 80, padding: 10, resize: 'vertical' }}
//                     placeholder="Add a note for this visit…"
//                     value={note}
//                     onChange={(e) => setNote(e.target.value)}
//                   />
//                   <div style={{ marginTop: 8 }}>
//                     <Button size="sm" variant="ghost" loading={saving} onClick={saveNote}>
//                       Save note
//                     </Button>
//                   </div>
//                 </>
//               ) : (
//                 <div className="rp-box rp-reportbox">{item.managerNote}</div>
//               )}
//             </div>
//           )}
//         </div>
//       </Modal>

//       <ConfirmDialog
//         open={confirmDel}
//         onClose={() => setConfirmDel(false)}
//         onConfirm={remove}
//         title="Delete report?"
//         message="This cannot be undone."
//         confirmLabel="Delete"
//       />
//     </>
//   );
// };

// /* ═════════════════════════════ List page ═════════════════════════════ */
// const VisitReportListPage = () => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const [sp, setSp] = useSearchParams();
//   const { role, user } = useSelector(selectAuthState);
//   const { myList, allList, loading } = useSelector((s) => s.visitReport);

//   const isAdmin = role === 'Admin';
//   const canSeeTeam = isAdmin || role === 'MarketingManager';
//   const canCreate = role === 'MarketingManager' || role === 'MarketingExecutive';

//   const fExecId = sp.get('executiveId') || '';
//   const fExecName = sp.get('name') || '';
//   const pFrom = sp.get('from') || '';
//   const pTo = sp.get('to') || '';
//   const pLabel = sp.get('label') || '';

//   const [range, setRange] = useState(() => {
//     const byLabel = PRESETS.find((p) => p.label === pLabel)?.key;
//     return byLabel || (pFrom || pTo ? 'custom' : 'all');
//   });
//   const [customFrom, setCustomFrom] = useState(pFrom);
//   const [customTo, setCustomTo] = useState(pTo);
//   const [scope, setScope] = useState(isAdmin ? 'team' : 'mine');
//   const [flow, setFlow] = useState('');
//   const [search, setSearch] = useState('');
//   const [debounced, setDebounced] = useState('');
//   const [page, setPage] = useState(1);
//   const [openId, setOpenId] = useState(null);

//   useEffect(() => {
//     const t = setTimeout(() => setDebounced(search), 300);
//     return () => clearTimeout(t);
//   }, [search]);

//   const { from, to } = useMemo(
//     () => computeRange(range, customFrom, customTo),
//     [range, customFrom, customTo]
//   );

//   const teamMode = !!fExecId || (canSeeTeam && scope === 'team');

//   const load = useCallback(() => {
//     const params = {
//       statusFlow: flow || undefined,
//       search: debounced || undefined,
//       executiveId: fExecId || undefined,
//       from: from || undefined,
//       to: to || undefined,
//       limit: 200,
//     };
//     dispatch(teamMode ? fetchAllReports(params) : fetchMyReports(params));
//   }, [dispatch, teamMode, flow, debounced, fExecId, from, to]);

//   useEffect(() => {
//     load();
//   }, [load]);

//   useEffect(() => setPage(1), [flow, debounced, range, scope, fExecId]);

//   const data = teamMode ? allList : myList;
//   const totalPages = Math.max(1, Math.ceil(data.length / PAGE_SIZE));
//   const rows = data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
//   const opened = data.find((x) => x._id === openId) || null;

//   const clearExecutive = () => {
//     const next = new URLSearchParams(sp);
//     next.delete('executiveId');
//     next.delete('name');
//     setSp(next, { replace: true });
//   };

//   return (
//     <div className="rp-page">
//       <div className="rp-toolbar">
//         {canSeeTeam && !isAdmin && !fExecId && (
//           <div className="rp-chips">
//             <button className={`rp-chip ${scope === 'mine' ? 'active' : ''}`} onClick={() => setScope('mine')}>
//               My Reports
//             </button>
//             <button className={`rp-chip ${scope === 'team' ? 'active' : ''}`} onClick={() => setScope('team')}>
//               Team Reports
//             </button>
//           </div>
//         )}
//         {fExecId && (
//           <button className="rp-filterpill" onClick={clearExecutive} title="Clear filter">
//             {fExecName || 'Executive'} <X size={13} />
//           </button>
//         )}
//         <div className="rp-spacer" />
//         {canSeeTeam && (
//           <button className="rp-chip" onClick={() => navigate('/visit-reports')}>← Dashboard</button>
//         )}
//         {canCreate && (
//           <button className="rp-chip active" onClick={() => navigate('/visit-reports/new')}>
//             <Plus size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
//             New Report
//           </button>
//         )}
//       </div>

//       <RangeBar
//         presets={PRESETS}
//         range={range}
//         onRange={setRange}
//         from={customFrom}
//         to={customTo}
//         onFrom={setCustomFrom}
//         onTo={setCustomTo}
//       />

//       <div className="rp-toolbar">
//         <div className="rp-chips">
//           {FLOW_FILTERS.map((f) => (
//             <button
//               key={f.key || 'all'}
//               className={`rp-chip ${flow === f.key ? 'active' : ''}`}
//               onClick={() => setFlow(f.key)}
//             >
//               {f.label}
//             </button>
//           ))}
//         </div>
//         <div className="rp-spacer" />
//         <div className="rp-search">
//           <Search size={14} />
//           <input
//             className="rp-input"
//             placeholder="Search name, mobile, taluk…"
//             value={search}
//             onChange={(e) => setSearch(e.target.value)}
//           />
//         </div>
//       </div>

//       <div className="rp-card">
//         {loading && data.length === 0 ? (
//           <div className="rp-loading">Loading…</div>
//         ) : data.length === 0 ? (
//           <div className="rp-empty">
//             <b>No reports found</b>
//             {teamMode
//               ? 'Reports submitted by your team will appear here.'
//               : 'Submit your first customer visit report to see it here.'}
//           </div>
//         ) : (
//           <div className="rp-table-wrap">
//             <table className="rp-table">
//               <thead>
//                 <tr>
//                   <th>Customer</th>
//                   <th>Mobile</th>
//                   <th>Location</th>
//                   <th>Lead</th>
//                   <th>Visit</th>
//                   {teamMode && <th>Submitted by</th>}
//                   <th>Date</th>
//                   <th>Report</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {rows.map((r) => (
//                   <tr key={r._id} className="clickable" onClick={() => setOpenId(r._id)}>
//                     <td>
//                       <div className="rp-strong">{r.name}</div>
//                       <div className="rp-sub">{r.businessName}</div>
//                     </td>
//                     <td>{r.mobile}</td>
//                     <td>
//                       {r.taluk}
//                       <div className="rp-sub">{r.district}</div>
//                     </td>
//                     <td><FlowPill flow={r.statusFlow} /></td>
//                     <td>
//                       {r.isRevisit ? <RevisitPill item={r} /> : <span className="rp-muted">1st visit</span>}
//                     </td>
//                     {teamMode && (
//                       <td>
//                         {r.executiveName || r.executiveId?.name || '—'}
//                         <div className="rp-sub">{r.executiveRole || r.executiveId?.role}</div>
//                       </td>
//                     )}
//                     <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.submittedAt)}</td>
//                     <td><div className="rp-snippet">{r.report}</div></td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         )}

//         {data.length > PAGE_SIZE && (
//           <div className="rp-pager">
//             <span>
//               {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, data.length)} of {data.length}
//             </span>
//             <div className="rp-pager-btns">
//               <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</button>
//               <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
//             </div>
//           </div>
//         )}
//       </div>

//       {opened && (
//         <DetailModal item={opened} role={role} user={user} onClose={() => setOpenId(null)} />
//       )}
//     </div>
//   );
// };

// export default VisitReportListPage;
